import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BookText, Check, ArrowUp, ArrowDown } from 'lucide-react';

const STORIES = [
  {
    title: 'The Lost Puppy',
    sentences: [
      'One rainy evening, a small puppy wandered into the park.',
      'It was shivering and looking for shelter under a bench.',
      'A kind girl named Priya noticed the puppy and picked it up.',
      'She wrapped it in her scarf and took it home.',
      'The next day, she put up posters to find its owner.',
      'A week later, the owner was found and was very grateful to Priya.',
    ]
  },
  {
    title: 'The Science Fair',
    sentences: [
      'Rohan had always been curious about how volcanoes work.',
      'He decided to build a volcano model for the science fair.',
      'He spent two weeks gathering materials and doing research.',
      'On the day of the fair, he mixed baking soda and vinegar inside his model.',
      'The volcano erupted with colorful foam, amazing everyone.',
      'Rohan won first prize and felt proud of his hard work.',
    ]
  },
  {
    title: 'The Magic Garden',
    sentences: [
      'Grandma had a beautiful garden behind her house.',
      'She planted seeds of different flowers every spring.',
      'She watered them daily and talked to the plants lovingly.',
      'By summer, the garden was full of colorful flowers and butterflies.',
      'Neighbors would come from far away just to see her garden.',
      'Grandma always said, "Love and patience make everything grow."',
    ]
  },
];

const StoryBuilder = () => {
  const navigate = useNavigate();
  const [storyIdx, setStoryIdx] = useState(0);
  const [order, setOrder] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [score, setScore] = useState(0);
  const [completed, setCompleted] = useState(false);

  const story = STORIES[storyIdx];

  // Initialize shuffled order
  useState(() => {
    const shuffled = [...Array(story.sentences.length).keys()].sort(() => Math.random() - 0.5);
    setOrder(shuffled);
  });

  const moveUp = (idx) => {
    if (idx === 0 || feedback) return;
    const newOrder = [...order];
    [newOrder[idx], newOrder[idx-1]] = [newOrder[idx-1], newOrder[idx]];
    setOrder(newOrder);
  };

  const moveDown = (idx) => {
    if (idx === order.length - 1 || feedback) return;
    const newOrder = [...order];
    [newOrder[idx], newOrder[idx+1]] = [newOrder[idx+1], newOrder[idx]];
    setOrder(newOrder);
  };

  const checkOrder = () => {
    const isCorrect = order.every((val, idx) => val === idx);
    if (isCorrect) {
      setScore(s => s + 25); setFeedback({ correct: true, msg: 'Perfect story order! You are a story master!' });
    } else {
      let correct = 0;
      order.forEach((val, idx) => { if (val === idx) correct++; });
      setFeedback({ correct: false, msg: `${correct}/${order.length} in correct position. Try rearranging!` });
    }
  };

  const nextStory = () => {
    if (storyIdx < STORIES.length - 1) {
      const nextIdx = storyIdx + 1;
      setStoryIdx(nextIdx);
      const shuffled = [...Array(STORIES[nextIdx].sentences.length).keys()].sort(() => Math.random() - 0.5);
      setOrder(shuffled);
      setFeedback(null);
    } else setCompleted(true);
  };

  if (completed) {
    return (
      <div className="min-h-screen bg-[#F4F8FB] flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-10 shadow-2xl max-w-md w-full text-center border-2 border-slate-100">
          <div className="text-7xl mb-4">📖</div>
          <h2 className="text-4xl font-black text-[#1E293B] mb-2">Story Expert!</h2>
          <p className="text-6xl font-black text-sky-500 mb-2">{score}</p>
          <p className="text-xl text-[#64748B] mb-8">out of {STORIES.length * 25} points</p>
          <div className="space-y-3">
            <button onClick={() => { setStoryIdx(0); setScore(0); setCompleted(false); setFeedback(null); const s = [...Array(STORIES[0].sentences.length).keys()].sort(() => Math.random()-0.5); setOrder(s); }} className="w-full py-4 bg-sky-500 text-white rounded-2xl font-bold border-b-4 border-sky-700 active:border-b-0 active:translate-y-1 transition-all">Play Again</button>
            <button onClick={() => navigate('/games')} className="w-full py-4 bg-slate-100 text-[#1E293B] rounded-2xl font-bold">Back to Brain Arena</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 to-blue-50" data-testid="story-builder-game">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate('/games')} className="flex items-center gap-2 text-[#64748B] font-semibold"><ArrowLeft className="w-5 h-5" />Back</button>
          <div className="flex gap-3"><span className="bg-sky-500 text-white px-4 py-2 rounded-2xl font-bold">Story {storyIdx+1}/{STORIES.length}</span><span className="bg-[#4D96FF] text-white px-4 py-2 rounded-2xl font-bold">{score}pts</span></div>
        </div>
        <div className="bg-white rounded-3xl p-8 border-2 border-slate-100 shadow-xl">
          <BookText className="w-12 h-12 text-sky-500 mx-auto mb-4" />
          <h2 className="text-3xl font-black text-[#1E293B] mb-2 text-center">"{story.title}"</h2>
          <p className="text-center text-[#64748B] mb-6">Arrange the sentences in the correct order!</p>
          <div className="space-y-3">
            {order.map((sentIdx, pos) => (
              <div key={pos} className="flex items-center gap-2" data-testid={`story-sentence-${pos}`}>
                <span className="w-8 h-8 bg-sky-100 text-sky-600 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0">{pos + 1}</span>
                <div className="flex-1 p-4 bg-[#F4F8FB] border-2 border-slate-200 rounded-2xl text-[#1E293B] font-medium">
                  {story.sentences[sentIdx]}
                </div>
                <div className="flex flex-col gap-1">
                  <button onClick={() => moveUp(pos)} className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center hover:bg-sky-100 transition-all" data-testid={`move-up-${pos}`}><ArrowUp className="w-4 h-4" /></button>
                  <button onClick={() => moveDown(pos)} className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center hover:bg-sky-100 transition-all" data-testid={`move-down-${pos}`}><ArrowDown className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
          {feedback && <div className={`mt-6 p-4 rounded-2xl font-bold text-center ${feedback.correct ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>{feedback.msg}</div>}
          <div className="flex gap-3 justify-center mt-6">
            {feedback?.correct ? (
              <button onClick={nextStory} className="px-8 py-3 bg-sky-500 text-white rounded-2xl font-bold border-b-4 border-sky-700 active:border-b-0 active:translate-y-1 transition-all">Next Story</button>
            ) : (
              <button onClick={checkOrder} className="px-8 py-3 bg-[#38E54D] text-white rounded-2xl font-bold border-b-4 border-green-600 active:border-b-0 active:translate-y-1 transition-all" data-testid="check-story-btn"><Check className="w-5 h-5 inline mr-1" />Check Order</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoryBuilder;
